/**
 * Auth Context
 * Manages user authentication state and provides auth functions
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = api.getToken();
      if (token) {
        try {
          const data = await api.getMe();
          setUser(data.user);
        } catch (err) {
          // Token is invalid or expired
          api.setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Sign in with email/password
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const data = await api.login(email, password);
      // Ensure user data is properly set
      if (data.user) {
      setUser(data.user);
      return data.user;
      }
      throw new Error('User data not received from server');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email/password
  const signup = async (name, email, password, role = 'student') => {
    try {
      setError(null);
      setLoading(true);
      const data = await api.register(name, email, password, role);
      // Ensure user data is properly set
      if (data.user) {
      setUser(data.user);
      return data.user;
      }
      throw new Error('User data not received from server');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const loginWithGoogle = async (credential, role = 'student') => {
    try {
      setError(null);
      setLoading(true);
      const data = await api.googleLogin(credential, role);
      if (data.user) {
        setUser(data.user);
        return data.user;
      }
      throw new Error('User data not received from server');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await api.logout();
    } catch (err) {
      // Ignore errors on logout
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
